// Define interfaces for the Web Speech API since TypeScript doesn't include them by default
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (event: Event) => void;
  onend: (event: Event) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onresult: (event: SpeechRecognitionEvent) => void;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionConstructor {
  new(): SpeechRecognition;
}

// Extend Window interface
declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export class SpeechRecognitionService {
  private recognition: SpeechRecognition | null = null;
  private transcript: string = '';
  private onListeningChange: ((listening: boolean) => void) | null = null;
  private onTranscriptChange: ((text: string) => void) | null = null;
  private finalTranscript: string = '';

  async requestMicrophonePermission(): Promise<boolean> {
    console.log('Requesting microphone permission...');
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        console.error('getUserMedia not supported');
        return false;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('Microphone permission granted', stream.active);
      stream.getTracks().forEach(track => track.stop()); // Clean up
      return true;
    } catch (error) {
      console.error('Error requesting microphone permission:', error);
      return false;
    }
  }

  async start(): Promise<boolean> {
    console.log('Starting speech recognition...');
    if (!this.recognition) {
      const initialized = this.initRecognition();
      if (!initialized) {
        return false;
      }
    }

    try {
      if (this.recognition) {
        this.recognition.start();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      return false;
    }
  }

  initRecognition() {
    console.log('Initializing speech recognition...');
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.error('Speech recognition not supported in this browser');
      return false;
    }

    try {
      // @ts-ignore - TypeScript doesn't know about webkitSpeechRecognition
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      
      console.log('Speech recognition object created:', !!this.recognition);

      if (this.recognition) {
        this.recognition.continuous = false; // Changed to false to prevent accumulation
        this.recognition.interimResults = true;
        this.recognition.lang = 'en-US';

        this.recognition.onstart = () => {
          console.log('Speech recognition started');
          this.transcript = ''; // Reset transcript when starting
          this.finalTranscript = ''; // Reset final transcript
          this.onTranscriptChange?.(''); // Clear any previous transcript
          this.onListeningChange?.(true);
        };

        this.recognition.onend = () => {
          console.log('Speech recognition ended');
          // Keep the final transcript
          if (this.finalTranscript) {
            this.transcript = this.finalTranscript;
            this.onTranscriptChange?.(this.finalTranscript);
          }
          this.onListeningChange?.(false);
        };

        this.recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          this.onListeningChange?.(false);
        };

        this.recognition.onresult = (event) => {
          let interimTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; ++i) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              this.finalTranscript = transcript;
              console.log('Final transcript:', transcript);
            } else {
              interimTranscript = transcript; // Only use the latest interim result
              console.log('Interim transcript:', transcript);
            }
          }

          // Use final transcript if available, otherwise use interim
          const currentTranscript = this.finalTranscript || interimTranscript;
          console.log('Current transcript:', currentTranscript);
          
          if (currentTranscript !== this.transcript) {
            this.transcript = currentTranscript;
            this.onTranscriptChange?.(currentTranscript);
          }
        };

        return true;
      }
      return false;
    } catch (error) {
      console.error('Error initializing speech recognition:', error);
      return false;
    }
  }

  stop() {
    console.log('Stopping speech recognition...');
    try {
      if (this.recognition) {
        this.recognition.stop();
        this.transcript = '';
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error stopping speech recognition:', error);
      return false;
    }
  }

  getTranscript(): string {
    return this.transcript;
  }

  setOnTranscriptChange(callback: (transcript: string) => void) {
    this.onTranscriptChange = callback;
  }

  setOnListeningChange(callback: (isListening: boolean) => void) {
    this.onListeningChange = callback;
  }
}

export const speechRecognition = new SpeechRecognitionService();
